import { msg } from "../locales/translate";
import { DataProvider } from "badmfck-signal";
import { IExecutorResponse } from "badmfck-signal/dist/DataProvider";
import { GD } from "src/GD";


export class BanksService {
	static ssid =
		"7cf3ed1c12fdb3a57e3158923f472d5c71af9b51b4809f71ff07d449bed871a0";

	private revision = 0;
	private projectID: number | undefined;
	private controller = new AbortController();
	banks = this.createProvider();

	constructor() {
		GD.REQ_BANK.listener = async req => this.getBank(req);
		GD.REQ_BANK_SAVE.listener = async req => this.saveBank(req);
		GD.REQ_BANK_DELETE.listener = async req => this.deleteBank(req);
		GD.REQ_BANK_RESTORE.listener = async req => this.restoreBank(req);
		GD.B_BANKS.onRequest(async () => { await this.loadBanks(); });
		GD.S_PROJECT_SELECTED.subscribe(project => {
			if (project.id !== this.projectID) this.invalidate(project.id);
		});
		GD.S_AUTH_LOG_OUT.subscribe(() => this.invalidate());
	}

	private createProvider() {
		const signal = this.controller.signal;
		return new DataProvider<{ projectID: number }, IBank[]>({ cacheTime: 0 }, "Banks").setExecutor(async request => {
			if (!request) return { data: null };
			try {
				return await GD.REQ_API.request({
					method: "bank/list",
					data: { project_id: request.projectID },
					expectedProjectId: request.projectID,
					signal,
				}) as IExecutorResponse<IBank[]>;
			} catch (cause) {
				return { data: null, error: this.requestError(cause) };
			}
		});
	}

	private invalidate(projectID?: number) {
		this.revision++;
		this.controller.abort();
		this.controller = new AbortController();
		this.projectID = projectID;
		// A fresh provider isolates pending callbacks from the previous project.
		this.banks = this.createProvider();
		GD.B_BANKS.invoke({ data: null });
	}

	private async loadBanks() {
		const revision = this.revision;
		const project = await GD.REQ_CURRENT_PROJECT.request();
		if (revision !== this.revision || !project) return;
		if (this.projectID !== project.id) this.invalidate(project.id);
		const currentRevision = this.revision;
		const provider = this.banks;
		const result = await provider.load({ projectID: project.id });
		if (currentRevision === this.revision && provider === this.banks) GD.B_BANKS.invoke(result);
	}

	private requestError(cause: unknown): IError {
		return { code: -1, message: cause instanceof Error ? cause.message : String(cause) };
	}

	private async updateBank(method: string, data: Record<string, unknown>, headers?: IAPIRequest["headers"]): Promise<IAPIResponse<IBank>> {
		const revision = this.revision;
		const project = await GD.REQ_CURRENT_PROJECT.request();
		if (revision !== this.revision || !project) return { error: { code: -1, message: msg("selectProject") } };
		try {
			const result = await GD.REQ_API.request({ method, data, headers, expectedProjectId: project.id, signal: this.controller.signal }) as IAPIResponse<IBank>;
			if (!result.error && revision === this.revision) {
				this.invalidate(project.id);
				await this.loadBanks();
			}
			return result;
		} catch (cause) {
			return { error: this.requestError(cause) };
		}
	}

	async restoreBank(req: number): Promise<IAPIResponse<IBank>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		return this.updateBank("bank/restore", { id: req });
	}

	async deleteBank(req: number): Promise<IAPIResponse<IBank>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		return this.updateBank("bank/delete", { id: req }, { Authorization: "Basic " + BanksService.ssid });
	}

	generateNewBank(): IBank {
		const res: IBank = {
			data: {
				country: "",
				iban: [],
				name: "",
				timezone: 0,
			},
			status: "active",
			id: -1,
			// commission: 0,
			// commissionPercent: 0,
		};
		return res;
	}

	async getBank(req: {
		bankID: number;
	}): Promise<IAPIResponse<IBank> | null> {
		//if (!req) return { error: { code: -1, message: "Invalid getAcquirer request" } };
		if (!req) return null;
		const bankID = req.bankID;

		if (bankID === -1) {
			return {
				data: this.generateNewBank(),
			};
		}

		const revision = this.revision;
		const project = await GD.REQ_CURRENT_PROJECT.request();
		if (revision !== this.revision || !project) return null;
		if (this.projectID === project.id) {
			const f: IBank | undefined = this.banks
				.getData()
				?.find(b => b.id === bankID);
			if (f) {
				return { data: f };
			}
		}

		try {
			const bank = await GD.REQ_API.request({
				method: "bank/get",
				expectedProjectId: project.id,
				signal: this.controller.signal,
				data: { id: bankID },
			});
			return revision === this.revision ? bank : null;
		} catch (cause) {
			return revision === this.revision ? { error: this.requestError(cause) } : null;
		}
	}

	async saveBank(req: IBank): Promise<IAPIResponse<IBank>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		return this.updateBank("bank/add", { id: req.id, data: req.data });
	}
}
